import { AddTask, Close } from "@mui/icons-material";
import { Box, Button, Dialog, DialogContent, DialogTitle } from "@mui/material";
import React from "react";
import * as Yup from "yup";
import PaginatedSearchResults from "../../common/components/data-display/PaginatedSearchResults";
import { useAlerts } from "../../common/context/AlertContext";
import AdvancedRequestService from "../../common/service/AdvancedRequestService";
import SalesLogService from "../../common/service/SalesLogService";
import AdvancedRequestSearchCriteria from "../Admin/ManageAdvancedRequests/AdvancedRequestSearchCriteria";
import { AdminAdvancedRequestSummary } from "../AdvancedRequests/AdvancedRequestSummary";
import { usePointOfSaleContext } from "./PointOfSaleContextProvider";

export default function RecentAdvancedRequestSearch() {
  const { addErrorAlert } = useAlerts();
  const { salesLog, setSalesLog } = usePointOfSaleContext();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const defaultSearchCriteria = {
    searchText: "",
    page: 1,
    itemsPerPage: 25,
    orderBy: "pickupDateDesc",
    startDateRange: null,
    endDateRange: null,
    fulfillmentStatus: "ORDER_PLACED",
    businesses: [],
  };
  if (salesLog.buyerBusiness) {
    defaultSearchCriteria.businesses.push(salesLog.buyerBusiness);
  }

  return (
    <Box data-testid="recent-advanced-request-search">
      <Button
        onClick={() => setDialogOpen(true)}
        size="medium"
        variant="outlined"
        startIcon={<AddTask />}
      >
        Product Request
      </Button>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Select a recent product request</DialogTitle>
        <DialogContent>
          {dialogOpen && (
            <PaginatedSearchResults
              navigateOnSearch={false}
              fetchSearchResults={AdvancedRequestService.getAdvancedRequests}
              defaultSearchCriteria={defaultSearchCriteria}
              SearchCriteriaComponent={AdvancedRequestSearchCriteria}
              ResultDisplayComponent={AdminAdvancedRequestSummary}
              onClickSearchResult={(item) => {
                SalesLogService.addAdvancedRequest(salesLog.id, item.id)
                  .then((updatedSalesLog) => {
                    setSalesLog(updatedSalesLog);
                  })
                  .catch((error) => {
                    addErrorAlert("Error adding product request", error);
                  })
                  .finally(() => setDialogOpen(false));
              }}
              validationSchema={Yup.object().shape({
                searchText: Yup.string(),
                startDateRange: Yup.date()
                  .nullable()
                  .when("endDateRange", {
                    is: (endDateRange) => endDateRange,
                    then: () =>
                      Yup.date().max(
                        Yup.ref("endDateRange"),
                        "Start date must be before end date"
                      ),
                  }),
                endDateRange: Yup.date().nullable(),
                businesses: Yup.array(),
              })}
              normalizeSearchCriteria={(criteria) => {
                const cleanedCriteria = Object.fromEntries(
                  Object.entries(criteria).filter(([_, v]) => v)
                );
                if (
                  cleanedCriteria.startDateRange &&
                  typeof cleanedCriteria.startDateRange !== "string"
                ) {
                  cleanedCriteria.startDateRange =
                    cleanedCriteria.startDateRange.toISOString();
                }
                if (
                  cleanedCriteria.endDateRange &&
                  typeof cleanedCriteria.endDateRange !== "string"
                ) {
                  cleanedCriteria.endDateRange =
                    cleanedCriteria.endDateRange.toISOString();
                }
                return cleanedCriteria;
              }}
            />
          )}
          <Button
            onClick={() => setDialogOpen(false)}
            size="large"
            sx={{ position: "fixed", top: "12px", right: "12px" }}
            color="secondary"
            endIcon={<Close />}
            variant="contained"
          >
            Back to Sale
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
